import { motion } from 'framer-motion'
import { CaretLeft, Plus } from 'phosphor-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { DragDropContext, type DropResult } from 'react-beautiful-dnd'
import { createPortal } from 'react-dom'
import { useQueryClient } from 'react-query'
import { NavLink, useLocation, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Button } from '../../../../components/Button'
import { DialogModal } from '../../../../components/DialogModal'
import {
  ScheduleMeetFormStepper,
  type DefaultValuesScheduleMeetForm,
} from '../../../../components/ScheduleMeetFormStepper'
import { TOAST_ERROR_DEFAULT_OPTIONS } from '../../../../constants/toast'
import { Medias } from '../../../../enumns/medias'
import { useMediaQuery } from '../../../../hooks/useMediaQuery'
import { fetchLastMeetingByTaskId } from '../../../../services/hooks/meetings/queries/useMeetingsByAttendeeEmail'
import { useProjectByIdQuery } from '../../../../services/hooks/projects/queries/useProjectQuery'
import { useTaskUpdateStatusMutation } from '../../../../services/hooks/tasks/mutations/useTaskUpdateStatusMutation'
import {
  TaskStatus,
  type TaskDomain,
} from '../../../../types/domain/task-domain'
import { formatDateString } from '../../../../utils/formatDateString'
import { getErrorMessage } from '../../../../utils/getErrorMessage'
import { truncateWithEllipsis } from '../../../../utils/truncateWithEllipsis'
import { ActionSelect } from '../components/ActionSelect'
import { Column } from '../components/Column'
import { TaskForm } from '../components/TaskForm/TaskForm'

type ColumnsData = Record<string, TaskDomain[]>

const columnNames: Record<string, string> = {
  start: 'Início',
  profile: 'Perfil',
  availability: 'Disponibilidade',
  scheduled: 'Agendado',
  accomplished: 'Realizado',
  canceled: 'Cancelado',
  refused: 'Recusado',
  onhold: 'On hold',
  finished: 'Finalizado',
}

const columnKeys = [
  'start',
  'profile',
  'availability',
  'scheduled',
  'accomplished',
  'canceled',
  'refused',
  'onhold',
  'finished',
]

export const Detail = () => {
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false)
  const [openScheduleTaskModal, setOpenScheduleTaskModal] = useState(false)
  const [columnsData, setColumnsData] = useState<ColumnsData>()
  const [taskToBeScheduled, setTaskToBeScheduled] = useState<TaskDomain>()
  const [selectedTask, setSelectedTask] = useState<TaskDomain>()

  const location = useLocation()
  const isMinScreenWidthLG = useMediaQuery(`(min-width: ${Medias.LG}px)`)
  const pathname = location.pathname
  const result = pathname.includes('/archived/')
  const kanbanContainerRef = useRef<HTMLDivElement | null>(null)

  const { id } = useParams()
  const queryClient = useQueryClient()

  const { data, refetch } = useProjectByIdQuery(Number(id))
  const { mutate } = useTaskUpdateStatusMutation()

  const clearLocationState = () => {
    window.history.replaceState(null, pathname)
  }

  const closeTaskModal = () => {
    setIsTaskModalOpen(false)
    setSelectedTask(undefined)
    clearLocationState()
  }

  const removeFromList = (
    list: TaskDomain[],
    index: number
  ): [TaskDomain, TaskDomain[]] => {
    const result = Array.from(list)
    const [removed] = result.splice(index, 1)
    return [removed, result]
  }

  const addToList = (
    list: TaskDomain[],
    index: number,
    element: TaskDomain
  ) => {
    const result = Array.from(list)
    result.splice(index, 0, element)
    return result
  }

  const handleDragEnd = async ({ destination, source }: DropResult) => {
    if (!destination || !columnsData) {
      return
    }

    if (destination.droppableId === TaskStatus.refused) {
      const column = columnsData[source.droppableId]
      const columnTaskToBeRefused = column[source.index]

      const lastMeeting = await fetchLastMeetingByTaskId(
        columnTaskToBeRefused.id
      )

      if (lastMeeting) {
        return toast.error(
          'Não é possível recusar uma tarefa com reunião agendada',
          TOAST_ERROR_DEFAULT_OPTIONS
        )
      }
    }

    const isSourceFinishedTask = source.droppableId === TaskStatus.finished

    if (isSourceFinishedTask) {
      return toast.error(
        'Não é possível mover uma tarefa finalizada',
        TOAST_ERROR_DEFAULT_OPTIONS
      )
    }

    const isDestinationScheduledColumn =
      destination.droppableId === TaskStatus.scheduled

    if (isDestinationScheduledColumn && columnsData) {
      const column = columnsData[source.droppableId]
      const columnTaskToBeScheduled = column[source.index]

      setOpenScheduleTaskModal(true)
      setTaskToBeScheduled(columnTaskToBeScheduled)
      return
    }

    if (destination.droppableId === TaskStatus.accomplished) {
      const column = columnsData[source.droppableId]
      const columnTaskToBeAccomplished = column[source.index]

      const lastMeeting = await fetchLastMeetingByTaskId(
        columnTaskToBeAccomplished.id
      )

      if (!lastMeeting) {
        return toast.error(
          'Não é possível alterar uma tarefa para realizado sem agendar uma reunião',
          TOAST_ERROR_DEFAULT_OPTIONS
        )
      }
    }

    // Move elements between lists
    let newColumnsData = { ...columnsData }

    const sourceList = newColumnsData[source.droppableId]
    const [removedElement, newSourceList] = removeFromList(
      sourceList,
      source.index
    )

    newColumnsData[source.droppableId] = newSourceList

    const destinationList = newColumnsData[destination.droppableId]
    newColumnsData[destination.droppableId] = addToList(
      destinationList,
      destination.index,
      removedElement
    )

    setColumnsData(newColumnsData)

    mutate(
      {
        id: removedElement.id,
        data: {
          status: destination.droppableId,
        },
      },
      {
        onSuccess(result) {
          if (result.status === TaskStatus.accomplished) {
            queryClient.invalidateQueries([
              '@meetings/status',
              'payment-meeting-details',
            ])
          }
          refetch()
        },
        onError(error) {
          toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
        },
      }
    )

    queryClient.invalidateQueries([
      '@meetings/status',
      'payment-meeting-details',
    ])
  }

  const handleTaskClick = useCallback((task: TaskDomain) => {
    setSelectedTask(task)
    setIsTaskModalOpen(true)
  }, [])

  useEffect(() => {
    if (!data) {
      return
    }

    const sortTasksIntoColumns = () => {
      let columnsData: ColumnsData = columnKeys.reduce(
        (acc, columnKey) => ({ ...acc, [columnKey]: [] }),
        {}
      )

      data.tasks?.forEach((task) => {
        columnsData[task.status].push(task)
      })

      setColumnsData(columnsData)
    }

    sortTasksIntoColumns()
  }, [data])

  useEffect(() => {
    if (!openScheduleTaskModal && !isTaskModalOpen) {
      refetch()
    }
  }, [openScheduleTaskModal, isTaskModalOpen])

  useEffect(() => {
    const locationState = location.state as { taskId: number }
    const taskId = locationState?.taskId

    if (data && taskId) {
      const currentTaskToOpen = data.tasks?.find((task) => task.id === taskId)

      if (currentTaskToOpen) {
        handleTaskClick(currentTaskToOpen)
      }
    }
  }, [location, data, handleTaskClick])

  useEffect(() => {
    const kanbanContainerEl = kanbanContainerRef.current

    if (kanbanContainerEl) {
      const kanbanContainerTop = kanbanContainerEl.getBoundingClientRect().top
      const kanbanContainerTopWithScroll = kanbanContainerTop + window.scrollY

      scrollTo({
        top: kanbanContainerTopWithScroll - 50,
        behavior: 'smooth',
      })
    }
  }, [location.key, kanbanContainerRef])

  const defaultValues: DefaultValuesScheduleMeetForm | undefined =
    taskToBeScheduled && taskToBeScheduled?.meeting
      ? {
          date: formatDateString(taskToBeScheduled.meeting.startAt, {
            dateStyle: 'short',
          }),
          startTime: formatDateString(taskToBeScheduled.meeting.startAt, {
            timeStyle: 'short',
          }),
          endTime: formatDateString(taskToBeScheduled.meeting.endAt, {
            timeStyle: 'short',
          }),
          summary: taskToBeScheduled.meeting.summary,
          description: taskToBeScheduled.meeting.description || undefined,
          experts: taskToBeScheduled?.meeting?.attendees
            .filter((attendee) => !!attendee.expert)
            .map((attendee) => ({
              id: attendee.expert!.id,
              name: attendee.expert!.name,
              email: attendee.email,
            })),
          analysts: taskToBeScheduled.meeting.attendees
            .filter((attendee) => !!attendee.analyst)
            .map((attendee) => ({
              id: attendee.analyst!.id,
              name: attendee.analyst!.fullName,
              email: attendee.email,
            })),
          administrativeUsers: [
            ...taskToBeScheduled.meeting.attendees
              .filter((attendee) => !!attendee.researcher)
              .map((attendee) => ({
                id: attendee.researcher!.id,
                name: attendee.researcher!.name,
                email: attendee.email,
              })),
            ...taskToBeScheduled.meeting.attendees
              .filter((attendee) => !!attendee.admin)
              .map((attendee) => ({
                id: attendee.admin!.id,
                name: attendee.admin!.name,
                email: attendee.email,
              })),
          ],
          contacts: taskToBeScheduled.meeting.attendees
            .filter((attendee) => !!attendee.contact)
            .map((attendee) => ({
              id: attendee.contact!.id,
              name: attendee.contact!.name,
              email: attendee.email,
            })),
        }
      : {
          experts: taskToBeScheduled?.expert
            ? [
                {
                  id: taskToBeScheduled.expert.id,
                  name: taskToBeScheduled.expert.name,
                  email: taskToBeScheduled.expert.email,
                },
              ]
            : [],
          analysts:
            data?.analysts.map(({ id, fullName }) => ({
              id,
              name: fullName,
              email: '',
            })) || [],
          administrativeUsers: taskToBeScheduled?.admin
            ? [
                {
                  id: taskToBeScheduled.admin?.id,
                  name: taskToBeScheduled.admin?.name,
                  email: taskToBeScheduled.admin?.email,
                },
              ]
            : taskToBeScheduled?.researcher
            ? [
                {
                  id: taskToBeScheduled.researcher?.id,
                  name: taskToBeScheduled.researcher?.name,
                  email: taskToBeScheduled.researcher?.email,
                },
              ]
            : [],
          contacts:
            data?.client?.contacts?.map(({ id, name, email }) => ({
              id,
              name,
              email,
            })) || [],
        }

  return (
    <>
      {data?.id === Number(id) && (
        <>
          <div ref={kanbanContainerRef} className="flex flex-col">
            {!!(!isMinScreenWidthLG && !result) && (
              <div className="flex flex-col gap-7">
                <NavLink to={'/painel/projects'}>
                  <CaretLeft size={28} color="#8D8D8D" />
                </NavLink>
                <div className="mb-8">
                  <p className="text-sm font-medium  text-grey-800">Projeto:</p>
                  <p className="text-base font-normal  text-grey-900">{`${data.id} - ${data.name}`}</p>
                </div>
              </div>
            )}

            {result ? (
              <div className="flex items-center gap-6 mb-10">
                <NavLink to={'/painel/projects/archived'}>
                  <CaretLeft />
                </NavLink>
                <p className="text-lg font-medium ">{data.name}</p>
              </div>
            ) : (
              <div className="flex items-center gap-2 mb-8 md:justify-end md:mr-6 lg:mb-2">
                <Button
                  type="button"
                  size="small"
                  onClick={() => {
                    setIsTaskModalOpen(true)
                    if (!isMinScreenWidthLG) {
                      document.body.style.overflow = 'hidden'
                    }
                  }}
                  className="flex-1 h-auto py-3 md:flex-none px-7 md:w-auto"
                  fullWidth
                >
                  <Plus />
                  <p className="text-sm font-medium ">Criar task</p>
                </Button>
                <ActionSelect data={data} />
              </div>
            )}

            <div className="w-auto mr-3 overflow-scroll scrollbar h-[550px]">
              <div className="flex gap-2 mr-6 w-fit">
                <DragDropContext onDragEnd={handleDragEnd}>
                  {columnKeys.map((listKey) => (
                    <Column
                      onTaskClick={handleTaskClick}
                      isDropDisabled={data.isFiled}
                      tasks={columnsData && columnsData[listKey]}
                      key={listKey}
                      title={columnNames[listKey]}
                      id={listKey}
                    />
                  ))}
                </DragDropContext>
              </div>
            </div>
          </div>

          {openScheduleTaskModal && taskToBeScheduled && (
            <ScheduleMeetFormStepper
              meetingId={taskToBeScheduled.meeting?.id}
              project={data}
              defaultValues={defaultValues}
              taskId={taskToBeScheduled.id}
              isOpen={openScheduleTaskModal}
              onClose={() => setOpenScheduleTaskModal(false)}
            />
          )}

          {/* Create Task Modal */}
          {isMinScreenWidthLG ? (
            <DialogModal
              isOpen={isTaskModalOpen}
              title={
                selectedTask?.name
                  ? truncateWithEllipsis(selectedTask.name, 100)
                  : 'Cadastrar task'
              }
              onClose={closeTaskModal}
              styleBox={{ maxWidth: '1300px' }}
            >
              <TaskForm
                selectedTask={selectedTask}
                project={data}
                onClose={closeTaskModal}
              />
            </DialogModal>
          ) : (
            <>
              {isTaskModalOpen &&
                createPortal(
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed top-0 bottom-0 left-0 right-0 z-50 p-6 overflow-y-auto bg-white scrollbar"
                  >
                    <button
                      aria-label="Voltar"
                      className="mb-8"
                      onClick={() => {
                        setIsTaskModalOpen(false)
                        document.body.style.overflow = 'auto'
                      }}
                    >
                      <CaretLeft size={24} className="text-grey-700" />
                    </button>

                    <TaskForm
                      selectedTask={selectedTask}
                      project={data}
                      onClose={closeTaskModal}
                    />
                  </motion.div>,
                  document.body
                )}
            </>
          )}
        </>
      )}
    </>
  )
}
